import * as zod from 'zod';

/**
 * Which streaming modality this External Control session expects. Determines the
 * control source the step acquires from arm-control-bot (and therefore which
 * external-control-bot path is authorized):
 *
 *  - pose            → pose streaming (EXTERNAL_CONTROL umbrella, or
 *                      COMPLIANCE_JOINT_TRAJECTORY when complianceMode is on).
 *  - joints          → joint-setpoint streaming on the dedicated `stream_joints`
 *                      topic (does NOT share /hardware/joint_trajectory with the
 *                      scaled_joint_trajectory_controller).
 *  - jointTrajectory → multi-waypoint trajectory via the
 *                      scaled_joint_trajectory_controller, unchanged.
 */
export const ExternalControlStreamType = zod.enum([
  'pose',
  'joints',
  'jointTrajectory',
]);
export type ExternalControlStreamType = zod.infer<
  typeof ExternalControlStreamType
>;

/**
 * Which subsystem an external client uses to take over:
 *  - api       → the client speaks the SDK/UDP protocol directly to
 *                external-control-bot (no bridge process).
 *  - rosHumble → external-control-bot spins up the ros-distro-bridge (Humble),
 *                which translates ROS topics into the SDK protocol.
 *  - rosJazzy  → same, for the Jazzy distro.
 *
 * All three (`api`, `rosHumble`, `rosJazzy`) are exposed in the step wizard and
 * the Streaming settings UI; external-control-bot maps each ROS distro to the
 * matching baked-in bridge conda env.
 */
export const ExternalControlControlledBy = zod.enum([
  'api',
  'rosHumble',
  'rosJazzy',
]);
export type ExternalControlControlledBy = zod.infer<
  typeof ExternalControlControlledBy
>;

/**
 * Optional trajectory-controller ServoJ tuning, applied when the session streams
 * joints through the online planner (`onlinePlannerStreaming`). Any omitted field
 * falls back to the server default in `TcServoSession`. Temporary surface while
 * the online-planner streaming API stabilizes.
 */
// The REST models declare these fields as i32 and the TC reads them into a
// 32-bit int, so the API rejects anything above INT32_MAX instead of letting
// it overflow on the wire.
const INT32_MAX = 2_147_483_647;

export const ExternalControlServoConfig = zod.object({
  // First-order low-pass time constant (τ), in milliseconds. The session
  // layer converts it to the TC's per-tick gain at enqueue:
  // k_p = 1 − exp(−dt/τ).
  tauMs: zod.number().int().min(1).max(1000).optional(),
  commandTimeoutMs: zod.number().int().positive().max(INT32_MAX).optional(),
  rampInDurationMs: zod.number().int().nonnegative().max(INT32_MAX).optional(),
});
export type ExternalControlServoConfig = zod.infer<
  typeof ExternalControlServoConfig
>;

/**
 * Server-side ServoJ defaults, applied per-field when the `start()` request
 * omits them. tauMs: smoothing time constant of the target low-pass filter —
 * smaller = snappier tracking, larger = smoother but laggier (the arm follows
 * the stream with roughly τ of lag); commandTimeoutMs: stream-staleness
 * window before the TC decelerates to rest — sized for network jitter on the
 * client→ECB→TC path while staying far under ECB's 5 s writer eviction;
 * rampInDurationMs: smooth-start ramp from the commanded position to the
 * first streamed target.
 * Also seeds the Streaming settings UI so operators start from these values.
 */
export const DEFAULT_SERVO_CONFIG: Required<ExternalControlServoConfig> = {
  tauMs: 20,
  commandTimeoutMs: 500,
  rampInDurationMs: 300,
};

export default zod.object({
  streamType: ExternalControlStreamType.default('pose'),
  controlledBy: ExternalControlControlledBy.default('api'),
  servoConfig: ExternalControlServoConfig.optional(),
});
